import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';

import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import PropertyFeesService from '../../../services/propertyFeesService';
import useModalProps from '../../../hooks/useModalProps';

export const usePetFeeModal = ({
  newPetsToCharge,
  petCharges,
  isLoadingPetCharges,
}) => {
  const petFeeModalProps = useModalProps();
  const defaultValues = useMemo(() => {
    if (isLoadingPetCharges) return {};
    const fees = petCharges?.map((fee) => ({
      [fee?.id]: 0,
    }));

    return Object.assign({}, ...fees);
  }, [petCharges, isLoadingPetCharges]);

  const formFields = useMemo(() => {
    if (isLoadingPetCharges) return [];

    const fields =
      petCharges?.map((fee) => {
        const petFeeNameCustomField = {
          variant: 'custom',
          component: (
            <Typography variant="bodyMedium">
              {fee.displayNameOnQuote}
            </Typography>
          ),
          GridProps: {
            xs: 4,
            mt: 1,
            py: 0,
          },
        };
        const petFeeAmountCustomField = {
          variant: 'custom',
          component: (
            <Typography variant="bodyMedium">
              {formatCurrency(fee.amount)}
            </Typography>
          ),
          GridProps: {
            xs: 2,
            mt: 1,
            py: 0,
          },
        };
        const counterField = {
          name: fee?.id,
          variant: 'counter',
          fullWidth: false,
          min: 0,
          max: newPetsToCharge.length,
          GridProps: {
            xs: 3,
            py: 0,
          },
        };
        return [petFeeNameCustomField, petFeeAmountCustomField, counterField];
      }) || [];

    return [...fields.flat()];
  }, [petCharges, isLoadingPetCharges, newPetsToCharge]);

  const reactHookFormProps = useReactHookFormProps({
    shouldUnregister: true,
    defaultValues,
  });

  return {
    reactHookFormProps,
    formFields,
    ...petFeeModalProps,
  };
};

export const usePropertyPetFees = ({ propertyId, organizationId }) => {
  const queryKey = ['propertyPetFees', propertyId];
  const propertyFeesService = new PropertyFeesService();
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      propertyFeesService.getAllByTransactionType({
        propertyId,
        organizationId,
        transactionType: 'PETFEE',
      }),
    {
      enabled: !!propertyId && !!organizationId,
      refetchOnWindowFocus: false,
    },
  );

  return {
    propertyPetFees: data,
    isLoadingPropertyPetFees: isLoading,
  };
};

export const usePropertyPetDep = ({ propertyId, organizationId }) => {
  const queryKey = ['propertyPetDep', propertyId];
  const propertyFeesService = new PropertyFeesService();
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      propertyFeesService.getAllByTransactionType({
        propertyId,
        organizationId,
        transactionType: 'PETDEP',
      }),
    {
      enabled: !!propertyId && !!organizationId,
      refetchOnWindowFocus: false,
    },
  );

  return {
    propertyPetDep: data,
    isLoadingPropertyPetDep: isLoading,
  };
};

export const usePropertyPetRent = ({ propertyId, organizationId }) => {
  const queryKey = ['propertyPetRent', propertyId];
  const propertyFeesService = new PropertyFeesService();
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      propertyFeesService.getAllByTransactionType({
        propertyId,
        organizationId,
        transactionType: 'PETRENT',
      }),
    {
      enabled: !!propertyId && !!organizationId,
      refetchOnWindowFocus: false,
    },
  );

  return {
    propertyPetRent: data,
    isLoadingPropertyPetRent: isLoading,
  };
};

export const usePetCharges = ({ propertyId, organizationId }) => {
  const { propertyPetFees, isLoadingPropertyPetFees } = usePropertyPetFees({
    organizationId,
    propertyId,
  });
  const { propertyPetDep, isLoadingPropertyPetDep } = usePropertyPetDep({
    organizationId,
    propertyId,
  });
  const { propertyPetRent, isLoadingPropertyPetRent } = usePropertyPetRent({
    organizationId,
    propertyId,
  });

  // One-time pet fees/deposits the user can choose to post immediately.
  // These drive the modal's counter rows and the `petFeeToCharge` payload.
  const petCharges = useMemo(
    () => [...(propertyPetFees || []), ...(propertyPetDep || [])],
    [propertyPetFees, propertyPetDep],
  );

  // Pet rent is a recurring monthly charge the backend applies automatically
  // when a pet is added/removed. It is kept SEPARATE from `petCharges` on
  // purpose so it never becomes a counter row or gets sent in the payload
  // (which would double-charge). It is surfaced as informational text only and
  // is used to decide whether the modal should open.
  const petRentFees = useMemo(() => propertyPetRent || [], [propertyPetRent]);

  return {
    petCharges,
    petRentFees,
    petRentConfigured: petRentFees.length > 0,
    isLoadingPetCharges:
      isLoadingPropertyPetFees ||
      isLoadingPropertyPetDep ||
      isLoadingPropertyPetRent,
  };
};
